import { localizedSubject, type AdLanguageId } from './ad-language';
import { kitCopyFromSubject, promotionOfferTitle } from './ad-subjects';
import { newestLockedVersion } from './ad-versions';
import {
  shapeIdBySize,
  KIT_HERO_SHAPE_ID,
  type AdBrand,
  type AdSubject,
  type AdSubjectType,
  type BannerKit,
  type BannerKitCatalog,
  type BannerKitParams,
  type BannerKitVersion,
  type EnabledKitSize,
  type KitQA,
} from './banner-kit-api';
import {
  DEFAULT_PLAYABLE_TIMING,
  type JsonObject,
  type Playable,
  type PlayableConfigFill,
  type PlayableParams,
  type PlayableProfile,
  type PlayableTemplate,
} from './playable-api';

/**
 * HTML5 Banners: non-gamified animated display banners. Two paths behind one tab:
 * - live-text banners, built as `banner` templates on the playable stack (a style, a background plate per size, the
 *   subject cut out, and the headline, offer line and CTA as live HTML);
 * - an Image Banners kit animated in the browser and exported through the kit's own HTML5 export.
 * Both deliver the standard clickTAG ZIP per size by default. Pure helpers only (unit-tested in the node env).
 */

/** The four display sizes of an HTML5 banner, the medium rectangle first (the default). */
export const HTML5_BANNER_FORMATS: readonly string[] = ['300x250', '728x90', '300x600', '320x50'];

/** The delivery profile both paths default to: one ZIP per size, `index.html` at the root, a clickTAG. */
export const HTML5_BANNER_PROFILE = 'html5-banner-zip';

/** The kit HTML5 export's older default, used when this environment does not serve the clickTAG profile yet. */
export const KIT_HTML5_FALLBACK_PROFILE = 'iab-dsp-zip';

/** The first of `preferred` the environment offers, else its first profile; '' when it offers none. Pure. */
export const preferredProfileId = (profiles: readonly PlayableProfile[], preferred: readonly string[]): string =>
  preferred.find((id) => profiles.some((p) => p.id === id)) ?? profiles[0]?.id ?? '';

/** The styles that can advertise a subject type (all styles when none fits), in the registry's order. Pure. */
export const stylesFor = (styles: readonly PlayableTemplate[], subjectType: AdSubjectType): PlayableTemplate[] => {
  const fit = styles.filter((tpl) => tpl.subjects.includes(subjectType));
  return fit.length > 0 ? fit : [...styles];
};

/** The config field a style draws the offer line from (the runtime falls back to `offer.title` when it is empty). */
const OFFER_LINE = 'offer_line';

/** The subject as an HTML5 banner shows it: its copy in the ad's language when the document holds it
 *  (`localized_copy`), its call to action the subject type's default in that language when it has none. An older
 *  document (no language) shows the subject as it was snapshotted. Pure. */
export const html5BannerSubject = (params: PlayableParams): AdSubject =>
  localizedSubject(params.subject, params.localized_copy, params.language, params.subject_type);

/**
 * The document with its copy locked to the subject's own words (an HTML5 banner's copy is never the owner's, so an ad
 * can never promise more than the live promotion does), in the ad's language ({@link html5BannerSubject}): the
 * headline is the subject's headline (else its name), the style's offer line its sub-line, the call to action its own
 * (else the subject type's default), as {@link kitCopyFromSubject} gives them for an image banner. A promotion with a
 * sub-line keeps its full offer as the offer title; otherwise the title is empty, since the runtime draws it in the
 * offer line's place and the headline would show twice. Idempotent. Pure.
 */
export const withHtml5BannerCopy = (params: PlayableParams): PlayableParams => {
  const subject = html5BannerSubject(params);
  const lock = kitCopyFromSubject(params.subject_type, subject);
  const config: JsonObject = { ...params.config };
  if (lock.subhead) config[OFFER_LINE] = lock.subhead;
  else delete config[OFFER_LINE];
  return {
    ...params,
    config,
    copy: { ...params.copy, headline: lock.headline, cta: lock.cta },
    offer: {
      ...params.offer,
      title: params.subject_type === 'promotion' && lock.subhead ? promotionOfferTitle(subject) : '',
    },
  };
};

/** A Lumio fill as an HTML5 banner may take it: the motion settings, colours, button and fonts, never the copy (the
 *  headline, offer line and call to action are the subject's own, see {@link withHtml5BannerCopy}). Pure. */
export const html5BannerFill = (fill: PlayableConfigFill): PlayableConfigFill => {
  const { copy: _copy, config, ...rest } = fill;
  if (!config) return rest;
  const { [OFFER_LINE]: _line, ...motion } = config;
  return { ...rest, config: motion };
};

/** A new HTML5 banner's document: its subject with the subject's own copy ({@link withHtml5BannerCopy}), the chosen
 *  style with the four sizes it takes, the brand (with its website theme), the UK/EU legal line, the subject's
 *  landing page and the ad's language (the website's default; '' while unknown, filled in later). Pure. */
export const newHtml5BannerParams = (
  subjectType: AdSubjectType,
  subject: AdSubject,
  brand: AdBrand,
  style: Pick<PlayableTemplate, 'id' | 'schema_version' | 'formats'>,
  landingUrl: string,
  language: AdLanguageId = '',
): PlayableParams => {
  const formats = HTML5_BANNER_FORMATS.filter((f) => style.formats.includes(f));
  return withHtml5BannerCopy({
    subject_type: subjectType,
    subject,
    template_id: style.id,
    schema_version: style.schema_version,
    config: {},
    copy: { headline: '', instruction: '', reveal_label: '', cta: '' },
    offer: { title: '', terms: '', tc_url: '' },
    brand,
    legal: { enabled: true, preset: 'uk_eu', text: '' },
    formats: formats.length > 0 ? formats : style.formats.slice(0, 1),
    art_direction: '',
    landing_url: landingUrl,
    timing: { ...DEFAULT_PLAYABLE_TIMING },
    audio: { default_muted: true },
    language,
  });
};

/** The card poster of an HTML5 banner: the 300x250 plate of the newest generated version (else its first plate),
 *  else of the newest version with a plate; '' when nothing has been painted. Pure. */
export const html5BannerPoster = (banner: Pick<Playable, 'ad_versions'>): string => {
  const plateOf = (v: Playable['ad_versions'][number]): string => {
    const plates = v.assets.plates ?? {};
    return plates['300x250'] || Object.values(plates).find(Boolean) || v.assets.background_url || '';
  };
  const painted = banner.ad_versions.filter((v) => plateOf(v) !== '');
  const best = newestLockedVersion(painted) ?? painted[0];
  return best ? plateOf(best) : '';
};

/* ---------- an Image Banners kit, animated ---------- */

/** The kits that can be animated: live ones with a generated (locked) version. Pure. */
export const kitsWithGeneratedVersion = (kits: readonly BannerKit[]): BannerKit[] =>
  kits.filter((k) => !k.archived && Boolean(newestLockedVersion(k.ad_versions)));

/** The document a kit's export reads for a version: the version's own, with the kit's current brand and legal line
 *  (mirrors the backend's `exportParams`). Pure. */
export const kitExportParams = (
  kit: Pick<BannerKit, 'ad_params'>,
  version: Pick<BannerKitVersion, 'params'>,
): BannerKitParams => ({
  ...version.params,
  brand: kit.ad_params.brand,
  legal: kit.ad_params.legal,
});

/** One size of a kit to animate: its pixel size, the render it is made from ('' when the version has none) and
 *  that render's QA boxes. */
export interface KitAnimateSize {
  size: string;
  w: number;
  h: number;
  /** The size's weight budget in the catalogue (the smallest over the kits that list it). */
  max_kb: number;
  url: string;
  boxes?: KitQA['boxes'];
}

/** The four HTML5 banner sizes of a kit version, each with the render the export would use: the shape the
 *  version's plan puts it in (else the catalogue's shape), the hero standing in for the square shape (mirrors the
 *  backend's `ResolveExport`). A size the catalogue does not list is left out. Pure. */
export const kitAnimateSizes = (catalog: BannerKitCatalog, version: BannerKitVersion): KitAnimateSize[] => {
  const shapeOf = shapeIdBySize(catalog, version.params);
  const out: KitAnimateSize[] = [];
  for (const size of HTML5_BANNER_FORMATS) {
    const listed = catalog.kits.flatMap((k) => k.sizes).filter((s) => s.id === size);
    if (listed.length === 0) continue;
    const shapeId = shapeOf[size] ?? listed[0].shape;
    const render = version.shapes[shapeId];
    const url =
      render?.url ||
      (shapeId === KIT_HERO_SHAPE_ID && version.hero?.url && !version.hero.error ? version.hero.url : '');
    out.push({
      size,
      w: listed[0].w,
      h: listed[0].h,
      max_kb: Math.min(...listed.map((s) => s.max_kb)),
      url: url ?? '',
      ...(render?.qa?.boxes ? { boxes: render.qa.boxes } : {}),
    });
  }
  return out;
};

/** The animated sizes as the kit export sheet lists them (pixel size and budget). Pure. */
export const kitAnimateExportSizes = (sizes: readonly KitAnimateSize[]): EnabledKitSize[] =>
  sizes.map((s) => ({ id: s.size, w: s.w, h: s.h, max_kb: s.max_kb, kits: [] }));
